import React, {
    useEffect,
    useMemo,
    useState,
    useContext,
    useCallback,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import "../pages.css";
import {
    MButton,
    LoadingContext,
    M_StateActions,
    MHeader,
    NavigateContext,
} from "src/M_Components";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "../../components/utils/apiResponse";
import DynamicForm from "../../components/Forms/DynamicForm";

import { STORAGE_USER } from "src/components/auth/storageKeys";

// #region Constants

/**
 * Redux-persist storage key used by the user realm tab and UI state.
 *
 * This key allows the account rebootstrap flow to remove stale persisted tabs
 * before the application reloads with the refreshed default account context.
 */
const USER_M_STATE_PERSIST_KEY = "persist:M_State_user";

// #endregion

// #region Private helpers

/**
 * Converts a PascalCase / UpperCamelCase property name to lowerCamelCase.
 * This is used to align UI form field names with API payload conventions.
 *
 * @param {string} s - Source string to convert.
 * @returns {string} The converted string in lowerCamelCase, or the original falsy value.
 */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/**
 * Returns the canonical form key used to represent a foreign key field in DynamicForm.
 * This keeps the mapping consistent with DynamicForm internal logic.
 *
 * Rules:
 * - If the option has Header, Header is used.
 * - Otherwise Name is used, ensuring it ends with "Id".
 *
 * @param {object} opt - Field option metadata returned by the schema endpoint.
 * @returns {string} Foreign key form field key.
 */
const getFkFormKey = (opt) => {
    const nameKey = opt?.Name || "";
    if (opt?.Header) return opt.Header;
    return nameKey?.endsWith("Id") ? nameKey : `${nameKey}Id`;
};

/**
 * Decodes a base64url-encoded string into a UTF-8 string.
 * This is used for parsing the JWT payload segment.
 *
 * @param {string} str - Base64url string to decode.
 * @returns {string} Decoded UTF-8 string or empty string on failure.
 */
const base64UrlDecode = (str) => {
    try {
        const pad = (s) => s + "=".repeat((4 - (s.length % 4)) % 4);
        const b64 = pad(str.replace(/-/g, "+").replace(/_/g, "/"));
        return decodeURIComponent(
            atob(b64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
    } catch {
        return "";
    }
};

/**
 * Parses a JWT and returns its payload as an object.
 * If the token is missing or malformed, an empty object is returned.
 *
 * @param {string} token - JWT string stored in localStorage.
 * @returns {object} Parsed payload object or empty object.
 */
const getJwtPayload = (token) => {
    if (!token) return {};
    const parts = token.split(".");
    if (parts.length < 2) return {};
    const json = base64UrlDecode(parts[1]);
    try {
        return JSON.parse(json || "{}");
    } catch {
        return {};
    }
};

/**
 * Reads the current DefaultAccountId from the JWT payload.
 * The expected claim is "acc". If not present or not numeric, null is returned.
 *
 * @returns {number|null} Default account id from token, or null.
 */
const getCurrentDefaultAccountIdFromToken = () => {
    const token = localStorage.getItem("token");
    const payload = getJwtPayload(token);
    const acc = payload?.acc;
    const n = Number(acc);
    return Number.isFinite(n) ? n : null;
};

/**
 * Filters schema options to decide which fields should appear in Create mode.
 * This excludes:
 * - keys
 * - audit fields
 * - foreign keys
 * - Inactive
 * - fields marked hidden for edit
 *
 * @param {object} opt - Field option metadata.
 * @returns {boolean} True when the field should be included in Create mode.
 */
const includeInCreate_Account = (opt) => {
    if (!opt) return false;
    if (opt.IsKey) return false;
    if (
        opt.Name === "CreatedAt" ||
        opt.Name === "UpdatedAt" ||
        opt.Name === "CreatedBy" ||
        opt.Name === "UpdatedBy"
    )
        return false;
    if (opt.IsForeignKey) return false;
    if (opt.Name === "Inactive") return false;
    if (opt.IsHiddenEdit) return false;
    return true;
};

/**
 * Extracts the item array from paged API responses used by list endpoints.
 *
 * Supported shapes:
 * - { items: [...] }
 * - { Items: [...] }
 * - direct array
 *
 * @param {object|Array|null|undefined} res - API response payload.
 * @returns {Array} Normalized item array.
 */
const getPagedItems = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.Items)) return res.Items;
    return [];
};

/**
 * Converts a camelCase key to PascalCase.
 *
 * @param {string} key - Source key.
 * @returns {string} PascalCase key.
 */
const toPascal = (key) => {
    if (!key) return key;
    return key.charAt(0).toUpperCase() + key.slice(1);
};

/**
 * Normalizes an API entity so its keys match the schema casing expected by DynamicForm.
 *
 * This is important for endpoints that return camelCase payloads while DynamicForm
 * receives PascalCase-based schema options.
 *
 * @param {object} row - Raw API entity.
 * @param {Array} options - Schema options.
 * @returns {object} Entity reshaped to the expected key names.
 */
const normalizeEntityToSchema = (row, options) => {
    if (!row || typeof row !== "object") return {};

    const source = { ...row };
    const normalized = { ...row };

    const getCandidateValue = (keys) => {
        for (const k of keys) {
            if (k in source) return source[k];
        }
        return undefined;
    };

    for (const opt of Array.isArray(options) ? options : []) {
        if (!opt) continue;

        const name = opt?.Name;
        const header = opt?.Header;
        const fkKey = opt?.IsForeignKey ? getFkFormKey(opt) : null;

        const nameCamel = toCamel(name);
        const headerCamel = toCamel(header);
        const fkCamel = fkKey ? toCamel(fkKey) : null;

        const value = getCandidateValue(
            [name, header, fkKey, nameCamel, headerCamel, fkCamel].filter(Boolean)
        );

        if (name && value !== undefined) normalized[name] = value;
        if (header && value !== undefined) normalized[header] = value;
        if (fkKey && value !== undefined) normalized[fkKey] = value;
    }

    for (const [key, value] of Object.entries(source)) {
        const pascal = toPascal(key);
        if (!(pascal in normalized)) {
            normalized[pascal] = value;
        }
    }

    return normalized;
};

/**
 * Builds a stable reload signature for account integrations.
 *
 * The integration list is filtered by account id, so the signature uses:
 * - current account id
 * - current UI language
 *
 * This intentionally avoids using the account name because names can contain
 * special characters, can change, and are not guaranteed to be unique.
 *
 * @param {number|string|null|undefined} accountId - Owner account id.
 * @param {string} lang - Current UI language.
 * @returns {string} Stable reload signature.
 */
const buildAccountIntegrationLoadSignature = (accountId, lang) =>
    `${accountId ?? ""}|${lang || ""}`;

// #endregion

// #region Component

/**
 * Accounts CRUD page that supports Create/Edit/View modes and preserves per-tab state.
 * The page uses DynamicForm for schema-driven rendering and persists tab snapshots in Redux.
 *
 * Soft session refresh is performed after updating the currently active default account.
 *
 * Additional read-only blocks:
 * - Default Address
 * - Default Shipper
 * - Default Contact
 * - Account Integrations
 */
export default function AccountConfigCrud({ handleTabClose, toastMessages }) {
    const dispatch = useDispatch();
    const M_State = useSelector((state) => state.StateKeyValue);
    const { t, i18n } = useTranslation();
    const ctx = useContext(LoadingContext);
    const { renameTab } = useContext(NavigateContext);

    // #region Seed values and primary local state

    /**
     * Reads global values used to seed this page on initial mount.
     * These are typically written by the list page before opening a tab.
     */
    const selectedTab = M_State.find((o) => o.key === "selectedTab")?.value;
    const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
    const globalLine = M_State.find((obj) => obj.key === "AccountLine")?.value;

    /**
     * Local state for the current tab id, operation mode, and selected row.
     * These values are restored either from global seeds or the per-tab snapshot.
     */
    const [id, setId] = useState(selectedTab);
    const [operation, setOperation] = useState(globalOperation);
    const [sessionLine, setSessionLine] = useState(globalLine);

    /**
     * Indicates whether the current operation mode is View.
     */
    const isViewMode = (operation || "").toLowerCase() === "view";

    /**
     * Indicates whether the current operation mode is Edit or View.
     * Account integrations are only loaded for persisted entities.
     */
    const isReadOrUpdateMode =
        (operation || "").toLowerCase() === "view" ||
        (operation || "").toLowerCase() === "edit";

    /**
     * Main account entity data and schema options returned from the API.
     */
    const [accountInfo, setAccountInfo] = useState({});
    const [allOptions, setAllOptions] = useState([]);

    /**
     * Related view blocks are populated in View mode for account-related entities.
     * Each block has its own schema options and entity details.
     */
    const [contactOptions, setContactOptions] = useState([]);
    const [addressOptions, setAddressOptions] = useState([]);
    const [shipperOptions, setShipperOptions] = useState([]);
    const [apiKeyOptions, setApiKeyOptions] = useState([]);
    const [contactDetail, setContactDetail] = useState(null);
    const [addressDetail, setAddressDetail] = useState(null);
    const [shipperDetail, setShipperDetail] = useState(null);
    const [apiKeyDetail, setApiKeyDetail] = useState(null);

    /**
     * Account integrations use one shared schema and a list of entities.
     * These blocks are rendered read-only in both Edit and View modes.
     */
    const [accountIntegrationOptions, setAccountIntegrationOptions] = useState([]);
    const [accountIntegrations, setAccountIntegrations] = useState([]);

    /**
     * Bookkeeping flags:
     * - isFirst prevents unnecessary reloads during initial mount.
     * - accountId keeps track of the entity id for update and view scenarios.
     */
    const [isFirst, setIsFirst] = useState(true);
    const [accountId, setAccountId] = useState(sessionLine?.Id);

    /**
     * Version used to force a controlled DynamicForm remount after restoring
     * a persisted snapshot. This is especially important in Create mode because
     * DynamicForm intentionally does not auto-reset from data after mount.
     */
    const [formRestoreVersion, setFormRestoreVersion] = useState(0);

    /**
     * Reference to the DynamicForm instance to:
     * - retrieve current values for snapshot persistence
     * - trigger form submission from header buttons
     */
    const formRef = useRef(null);

    // #endregion

    // #region Live refs and async guards

    /**
     * Live refs are used to avoid stale closures in async operations.
     * This prevents handlers from using outdated state values after re-renders.
     */
    const idRef = useRef(id);
    const accountIdRef = useRef(accountId);
    const operationRef = useRef(operation);
    const sessionLineRef = useRef(sessionLine);
    const accountInfoRef = useRef(accountInfo);

    useEffect(() => {
        idRef.current = id;
    }, [id]);

    useEffect(() => {
        accountIdRef.current = accountId;
    }, [accountId]);

    useEffect(() => {
        operationRef.current = operation;
    }, [operation]);

    useEffect(() => {
        sessionLineRef.current = sessionLine;
    }, [sessionLine]);

    useEffect(() => {
        accountInfoRef.current = accountInfo;
    }, [accountInfo]);

    /**
     * Per-tab token used as a guard for async requests.
     * When the tab changes, the token increments and older requests are ignored.
     */
    const tabTokenRef = useRef(0);
    useEffect(() => {
        tabTokenRef.current += 1;
    }, [id]);

    /**
     * Guards repeated integration reloads for the same account and language.
     */
    const lastAccountIntegrationLoadRef = useRef("");

    /**
     * Prevents tab snapshot writes while a default-account rebootstrap is running.
     *
     * This guard keeps stale Create/Edit/View tab data from being written back into
     * Redux during tab cleanup, component unmount, delayed form changes, or debounced
     * snapshot persistence.
     */
    const suppressSnapshotPersistenceRef = useRef(false);

    // #endregion

    // #region Services

    /**
     * Service instances for API communication.
     * useMemo ensures stable instances across renders for the same base URL.
     */
    const svcAccounts = useMemo(
        () => new BufferService("Accounts", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcCreate = useMemo(
        () => new BufferService("Accounts/Create", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcUpdate = useMemo(
        () => new BufferService("Accounts/Update", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcContacts = useMemo(
        () => new BufferService("Contacts", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcAddresses = useMemo(
        () => new BufferService("Addresses", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcShippers = useMemo(
        () => new BufferService("Shippers", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );
    const svcApiKeys = useMemo(
        () => new BufferService("ApiKeys", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcAccountIntegrations = useMemo(
        () => new BufferService("AccountIntegrations", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcMenus = useMemo(
        () => new BufferService("Menus/All", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    const svcPermissions = useMemo(
        () => new BufferService("Permissions/All", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    // #endregion

    // #region Snapshot helpers

    /**
     * Increments the restore version used to remount DynamicForm when snapshot data
     * is restored into the page. This ensures Create mode also reflects restored values.
     */
    const bumpFormRestoreVersion = useCallback(() => {
        setFormRestoreVersion((x) => x + 1);
    }, []);

    /**
     * Persists the current tab state into Redux.
     *
     * The function reads current values directly from DynamicForm so the latest draft
     * can be saved even when the user switches tabs before a blur event occurs.
     *
     * @param {string|number|null|undefined} targetId - Snapshot tab id override.
     */
    const persistCurrentTabSnapshot = useCallback(
        (targetId) => {
            if (suppressSnapshotPersistenceRef.current) return;

            const tabId = targetId ?? idRef.current;
            if (!tabId) return;

            const currentFormValues = formRef.current?.getValues?.() || {};

            const sanitize = (o) =>
                Object.fromEntries(
                    Object.entries(o || {}).filter(([, v]) => v !== "")
                );

            const accountInfoToPersist =
                (operationRef.current || "").toLowerCase() === "view"
                    ? accountInfoRef.current || {}
                    : {
                        ...(accountInfoRef.current || {}),
                        ...sanitize(currentFormValues),
                    };

            const sessionData = [
                { key: "accountInfo", value: accountInfoToPersist },
                { key: "isFirst", value: isFirst },
                { key: "operation", value: operationRef.current },
                { key: "sessionLine", value: sessionLineRef.current },
                { key: "allOptions", value: allOptions },

                { key: "contactOptions", value: contactOptions },
                { key: "addressOptions", value: addressOptions },
                { key: "shipperOptions", value: shipperOptions },
                { key: "apiKeyOptions", value: apiKeyOptions },

                { key: "contactDetail", value: contactDetail },
                { key: "addressDetail", value: addressDetail },
                { key: "shipperDetail", value: shipperDetail },
                { key: "apiKeyDetail", value: apiKeyDetail },

                { key: "accountIntegrationOptions", value: accountIntegrationOptions },
                { key: "accountIntegrations", value: accountIntegrations },

                { key: "accountId", value: accountIdRef.current },
            ];

            dispatch(M_StateActions.addM_State({ key: tabId, value: sessionData }));
        },
        [
            dispatch,
            isFirst,
            allOptions,
            contactOptions,
            addressOptions,
            shipperOptions,
            apiKeyOptions,
            contactDetail,
            addressDetail,
            shipperDetail,
            apiKeyDetail,
            accountIntegrationOptions,
            accountIntegrations,
        ]
    );

    // #endregion

    // #region Navigation and title

    /**
     * Closes the current tab and removes the persisted snapshot from Redux.
     * This avoids restoring a stale tab state after navigation or reload.
     */
    const closeThisTab = useCallback(() => {
        const tabId = idRef.current;

        try {
            dispatch(M_StateActions.removeM_State({ key: tabId }));
        } catch {
            // Intentionally ignored.
        }

        try {
            handleTabClose?.(tabId);
        } catch {
            // Intentionally ignored.
        }
    }, [dispatch, handleTabClose]);

    /**
     * Updates the tab title based on operation mode and the account name.
     * A guard is used to avoid dispatch loops when the computed title is unchanged.
     */
    const lastTabTitleRef = useRef("");
    useEffect(() => {
        if (!selectedTab || !operation) return;

        const i18nKey =
            operation === "Create"
                ? "LabelNewAccount"
                : operation === "Edit"
                    ? "LabelEditAccount"
                    : "LabelViewAccount";

        const name = sessionLine?.Name ?? accountInfo?.Name ?? "";
        const base = t(i18nKey);
        const nextTitle = operation === "Create" || !name ? base : `${base} "${name}"`;

        if (lastTabTitleRef.current === nextTitle) return;
        lastTabTitleRef.current = nextTitle;

        renameTab(String(selectedTab), nextTitle);
    }, [selectedTab, operation, sessionLine?.Name, accountInfo?.Name, t, renameTab]);

    // #endregion

    // #region Redux-backed FK filter restore

    /**
     * Reads the per-tab foreign key query snapshot used by DynamicForm.
     * This restores the filter terms typed by the user inside FK dropdowns.
     */
    const fkInitialQueries = useMemo(() => {
        const tab = M_State.find((o) => o.key === id)?.value || [];
        return tab.find((x) => x.key === "dfFkQueries")?.value || {};
    }, [M_State, id]);

    // #endregion

    // #region Main entity and related data loading

    /**
     * Fetches account details for a given id and updates local state.
     * Requests are guarded by the per-tab token to avoid applying stale results.
     *
     * @param {number} idToGet - Account id to fetch.
     * @returns {Promise<object|null>} Account entity or null when failed.
     */
    const fetchDetails = useCallback(
        async (idToGet) => {
            const myToken = tabTokenRef.current;

            ctx.setIsLoad(true);
            try {
                const res = await svcAccounts.getId(idToGet);
                if (isFailurePayload(res)) {
                    toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
                    return null;
                }

                if (myToken !== tabTokenRef.current) return null;

                setAccountInfo(res || {});
                setAccountId(res?.Id);
                return res || null;
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
                return null;
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [svcAccounts]
    );

    /**
     * Fetches schema options and entity details for related view blocks.
     * Only runs when at least one related foreign key id is present.
     *
     * @param {object} param0 - Related ids used to load blocks.
     * @param {number|null} param0.contactId - Contact id.
     * @param {number|null} param0.addressId - Address id.
     * @param {number|null} param0.shipperId - Shipper id.
     * @param {number|null} param0.apiKeyId - ApiKey id.
     * @returns {Promise<void>}
     */
    const fetchRelatedBlocks = useCallback(
        async ({ contactId, addressId, shipperId, apiKeyId } = {}) => {
            const myToken = tabTokenRef.current;

            if (!contactId && !addressId && !shipperId && !apiKeyId) return;

            ctx.setIsLoad(true);
            try {
                const tasks = [];

                if (contactId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcContacts.options(),
                                svcContacts.getId(contactId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setContactOptions(opts || []);
                            if (!isFailurePayload(det)) setContactDetail(det || null);
                        })()
                    );
                }

                if (addressId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcAddresses.options(),
                                svcAddresses.getId(addressId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setAddressOptions(opts || []);
                            if (!isFailurePayload(det)) setAddressDetail(det || null);
                        })()
                    );
                }

                if (shipperId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcShippers.options(),
                                svcShippers.getId(shipperId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setShipperOptions(opts || []);
                            if (!isFailurePayload(det)) setShipperDetail(det || null);
                        })()
                    );
                }

                if (apiKeyId) {
                    tasks.push(
                        (async () => {
                            const [opts, det] = await Promise.all([
                                svcApiKeys.options(),
                                svcApiKeys.getId(apiKeyId),
                            ]);
                            if (myToken !== tabTokenRef.current) return;
                            if (!isFailurePayload(opts)) setApiKeyOptions(opts || []);
                            if (!isFailurePayload(det)) setApiKeyDetail(det || null);
                        })()
                    );
                }

                await Promise.all(tasks);
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [svcContacts, svcAddresses, svcShippers, svcApiKeys]
    );

    /**
     * Fetches schema options and entity list for account integrations belonging to one account.
     * The data is rendered read-only and is reused in both Edit and View modes.
     *
     * The backend filter uses the account id:
     * - AccountId eq {CurrentAccountId}
     *
     * A reload guard is used to avoid repeated list calls for the same account id and language.
     *
     * @param {number|string|null|undefined} ownerAccountId - Current account id.
     * @param {object} config - Optional load configuration.
     * @param {boolean} config.force - When true, bypasses the reload guard.
     * @returns {Promise<void>}
     */
    const fetchAccountIntegrations = useCallback(
        async (ownerAccountId, { force = false } = {}) => {
            const myToken = tabTokenRef.current;

            /**
             * Account integrations must be loaded by AccountId, not by Account name.
             * This avoids broken filters when account names contain special characters
             * like apostrophes and also avoids ambiguity if two accounts share a name.
             */
            const normalizedAccountId = Number(ownerAccountId);

            if (!Number.isFinite(normalizedAccountId) || normalizedAccountId <= 0) {
                lastAccountIntegrationLoadRef.current = "";
                setAccountIntegrations([]);
                return;
            }

            const loadSignature = buildAccountIntegrationLoadSignature(
                normalizedAccountId,
                i18n.language
            );

            if (!force && lastAccountIntegrationLoadRef.current === loadSignature) {
                return;
            }

            lastAccountIntegrationLoadRef.current = loadSignature;

            ctx.setIsLoad(true);
            try {
                const [opts, listRes] = await Promise.all([
                    svcAccountIntegrations.options(),
                    svcAccountIntegrations.filter(
                        `?Filter=${encodeURIComponent(`AccountId eq ${normalizedAccountId}`)}`
                    ),
                ]);

                if (myToken !== tabTokenRef.current) return;

                const normalizedOptions = !isFailurePayload(opts) ? opts || [] : [];
                const normalizedItems = !isFailurePayload(listRes)
                    ? getPagedItems(listRes).map((row) =>
                        normalizeEntityToSchema(row, normalizedOptions)
                    )
                    : [];

                setAccountIntegrationOptions(normalizedOptions);
                setAccountIntegrations(normalizedItems);
            } catch (err) {
                lastAccountIntegrationLoadRef.current = "";
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [svcAccountIntegrations, i18n.language]
    );

    /**
     * Fetches the schema options for the main DynamicForm.
     * Optionally fetches entity details when the operation is not Create.
     *
     * @param {string} op - Operation mode (Create/Edit/View).
     * @param {object|null} line - Selected row data.
     * @param {object} config - Additional options.
     * @param {boolean} config.skipDetails - When true, skips fetching entity details.
     * @returns {Promise<void>}
     */
    const fetchOptions = useCallback(
        async (op, line, { skipDetails } = { skipDetails: false }) => {
            const myToken = tabTokenRef.current;

            ctx.setIsLoad(true);
            try {
                const res = await svcAccounts.options();
                if (isFailurePayload(res) || res?.name === "AxiosError") {
                    toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
                    return;
                }

                if (myToken !== tabTokenRef.current) return;

                setAllOptions(res || []);

                const mode = op || operationRef.current || "View";
                const lineId = line?.Id ?? sessionLineRef.current?.Id;

                if (!skipDetails && mode !== "Create" && lineId) {
                    await fetchDetails(lineId);
                }
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            } finally {
                if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fetchDetails, svcAccounts]
    );

    // #endregion

    // #region Initial load and tab restore

    /**
     * Performs the initial restore from the per-tab snapshot and triggers initial fetches.
     *
     * Snapshot data is preferred whenever it exists. This avoids overwriting unsaved form edits
     * after the user navigates away from the tab and returns later.
     */
    useEffect(() => {
        const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
        const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

        const hasSnapshot = sessionForThisTab.length > 0;

        const snapInfo = pick("accountInfo");
        const snapIF = pick("isFirst");
        const snapOP = pick("operation");
        const snapSL = pick("sessionLine");
        const snapSchema = pick("allOptions");

        const snapContactOptions = pick("contactOptions");
        const snapAddressOptions = pick("addressOptions");
        const snapShipperOptions = pick("shipperOptions");
        const snapApiKeyOptions = pick("apiKeyOptions");

        const snapContactDetail = pick("contactDetail");
        const snapAddressDetail = pick("addressDetail");
        const snapShipperDetail = pick("shipperDetail");
        const snapApiKeyDetail = pick("apiKeyDetail");

        const snapAccountIntegrationOptions = pick("accountIntegrationOptions");
        const snapAccountIntegrations = pick("accountIntegrations");

        if (snapInfo) setAccountInfo(snapInfo);
        if (snapIF !== undefined) setIsFirst(snapIF);
        else if (isFirst) setIsFirst(false);

        const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
        const seedLine = globalLine !== undefined ? globalLine : snapSL;

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) {
            setSessionLine(seedLine);
            if (seedLine.Id) setAccountId(seedLine.Id);
        }

        if (Array.isArray(snapSchema)) setAllOptions(snapSchema);

        if (Array.isArray(snapContactOptions)) setContactOptions(snapContactOptions);
        if (Array.isArray(snapAddressOptions)) setAddressOptions(snapAddressOptions);
        if (Array.isArray(snapShipperOptions)) setShipperOptions(snapShipperOptions);
        if (Array.isArray(snapApiKeyOptions)) setApiKeyOptions(snapApiKeyOptions);

        if (snapContactDetail) setContactDetail(snapContactDetail);
        if (snapAddressDetail) setAddressDetail(snapAddressDetail);
        if (snapShipperDetail) setShipperDetail(snapShipperDetail);
        if (snapApiKeyDetail) setApiKeyDetail(snapApiKeyDetail);

        if (Array.isArray(snapAccountIntegrationOptions)) {
            setAccountIntegrationOptions(snapAccountIntegrationOptions);
        }
        if (Array.isArray(snapAccountIntegrations)) {
            setAccountIntegrations(snapAccountIntegrations);
        }

        if (globalOperation !== undefined) {
            dispatch(M_StateActions.removeM_State({ key: "operation" }));
        }
        if (globalLine !== undefined) {
            dispatch(M_StateActions.removeM_State({ key: "AccountLine" }));
        }

        /**
         * When snapshot data already exists, refresh only the schema and then force
         * a DynamicForm remount so Create mode receives the restored values as initial state.
         */
        if (hasSnapshot) {
            fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(() => {
                bumpFormRestoreVersion();
                ctx.setIsLoad(false);
            });
            return;
        }

        fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
            if (seedOperation === "Create") {
                bumpFormRestoreVersion();
                ctx.setIsLoad(false);
                return;
            }

            if (seedOperation && seedLine?.Id) {
                const entity = await fetchDetails(seedLine?.Id);

                if (seedOperation === "View" && entity) {
                    await fetchRelatedBlocks({
                        contactId: entity?.DefaultContactId,
                        addressId: entity?.DefaultAddressId,
                        shipperId: entity?.DefaultShipperId,
                        apiKeyId: entity?.CargofiveApiKeyId,
                    });
                }

                /**
                 * Account integrations are intentionally loaded by AccountId.
                 * Do not use entity.Name here because names are not stable filter keys.
                 */
                const integrationAccountId = entity?.Id ?? seedLine?.Id;

                if ((seedOperation === "View" || seedOperation === "Edit") && integrationAccountId) {
                    await fetchAccountIntegrations(integrationAccountId, { force: true });
                }

                bumpFormRestoreVersion();
                ctx.setIsLoad(false);
                return;
            }

            bumpFormRestoreVersion();
            ctx.setIsLoad(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Persists a debounced snapshot whenever relevant page state changes.
     * The current DynamicForm values are read directly to keep draft state updated.
     */
    const saveSnapshotTimer = useRef(null);

    useEffect(() => {
        if (!id) return;
        if (suppressSnapshotPersistenceRef.current) return;

        if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);

        saveSnapshotTimer.current = setTimeout(() => {
            persistCurrentTabSnapshot(id);
        }, 100);

        return () => {
            if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        };
    }, [
        id,
        accountInfo,
        isFirst,
        operation,
        sessionLine,
        allOptions,
        contactOptions,
        addressOptions,
        shipperOptions,
        apiKeyOptions,
        contactDetail,
        addressDetail,
        shipperDetail,
        apiKeyDetail,
        accountIntegrationOptions,
        accountIntegrations,
        accountId,
        persistCurrentTabSnapshot,
    ]);

    /**
     * Restores the snapshot for a given tab id.
     * When no snapshot exists, the data is fetched from the API using the current operation and line.
     *
     * @param {string|number} sessionID - Tab id used as the snapshot key.
     * @returns {Promise<void>}
     */
    const getSessionStorageData = useCallback(
        async (sessionID) => {
            ctx.setIsLoad(true);
            const data = M_State.find((obj) => obj.key === sessionID)?.value;

            if (data) {
                const pick = (k) => data.find((x) => x.key === k)?.value;

                setAccountInfo(pick("accountInfo") || {});
                setIsFirst(pick("isFirst") ?? false);
                setOperation(pick("operation"));
                setSessionLine(pick("sessionLine"));

                const restoredAccId =
                    pick("sessionLine")?.Id ??
                    pick("accountInfo")?.Id ??
                    (pick("accountId") ?? null);

                setAccountId(restoredAccId ?? null);

                const schema = pick("allOptions");
                if (Array.isArray(schema)) setAllOptions(schema);

                setContactOptions(pick("contactOptions") || []);
                setAddressOptions(pick("addressOptions") || []);
                setShipperOptions(pick("shipperOptions") || []);
                setApiKeyOptions(pick("apiKeyOptions") || []);
                setContactDetail(pick("contactDetail") || null);
                setAddressDetail(pick("addressDetail") || null);
                setShipperDetail(pick("shipperDetail") || null);
                setApiKeyDetail(pick("apiKeyDetail") || null);

                setAccountIntegrationOptions(pick("accountIntegrationOptions") || []);
                setAccountIntegrations(pick("accountIntegrations") || []);

                await fetchOptions(pick("operation"), pick("sessionLine"), {
                    skipDetails: true,
                });

                bumpFormRestoreVersion();
                ctx.setIsLoad(false);
                return;
            }

            const op = operationRef.current;
            const line = sessionLineRef.current;

            await fetchOptions(op, line, { skipDetails: true }).finally(async () => {
                if (op !== "Create" && line?.Id) {
                    const entity = await fetchDetails(line?.Id);

                    if (op === "View" && entity) {
                        await fetchRelatedBlocks({
                            contactId: entity?.DefaultContactId,
                            addressId: entity?.DefaultAddressId,
                            shipperId: entity?.DefaultShipperId,
                            apiKeyId: entity?.CargofiveApiKeyId,
                        });
                    }

                    /**
                     * Account integrations are intentionally loaded by AccountId.
                     * Do not use entity.Name here because names are not stable filter keys.
                     */
                    const integrationAccountId = entity?.Id ?? line?.Id;

                    if ((op === "View" || op === "Edit") && integrationAccountId) {
                        await fetchAccountIntegrations(integrationAccountId, { force: true });
                    }
                }

                bumpFormRestoreVersion();
                ctx.setIsLoad(false);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            M_State,
            fetchDetails,
            fetchOptions,
            fetchRelatedBlocks,
            fetchAccountIntegrations,
            bumpFormRestoreVersion,
        ]
    );

    /**
     * Handles tab switching by saving the current tab draft first and then restoring the next tab.
     */
    useEffect(() => {
        if (!selectedTab) return;
        if (selectedTab === id) return;
        if (suppressSnapshotPersistenceRef.current) return;

        persistCurrentTabSnapshot(idRef.current);
        setId(selectedTab);
        getSessionStorageData(selectedTab);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    /**
     * Persists the current tab snapshot during unmount.
     * This protects the latest draft state when the component leaves the tree.
     */
    useEffect(() => {
        return () => {
            persistCurrentTabSnapshot(idRef.current);
        };
    }, [persistCurrentTabSnapshot]);

    // #endregion

    // #region Language and dependent reloads

    /**
     * Reloads schema and updates the tab title when the language changes.
     * The first mount is excluded to avoid unnecessary initial duplicate loading.
     */
    useEffect(() => {
        if (!operationRef.current || isFirst) return;

        fetchOptions(operationRef.current, sessionLineRef.current, {
            skipDetails: true,
        });

        const op = operationRef.current;
        const i18nKey =
            op === "Create"
                ? "LabelNewAccount"
                : op === "Edit"
                    ? "LabelEditAccount"
                    : "LabelViewAccount";

        const name = sessionLineRef.current?.Name ?? accountInfo?.Name ?? "";
        const base = t(i18nKey);
        const nextTitle = op === "Create" || !name ? base : `${base} "${name}"`;

        if (lastTabTitleRef.current !== nextTitle) {
            lastTabTitleRef.current = nextTitle;
            renameTab(String(selectedTab), nextTitle);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    /**
     * In View mode, loads related blocks whenever foreign keys change or the language changes.
     * This keeps the read-only view blocks consistent with the current account entity.
     */
    useEffect(() => {
        if (!isViewMode) return;

        const contactId = accountInfo?.DefaultContactId;
        const addressId = accountInfo?.DefaultAddressId;
        const shipperId = accountInfo?.DefaultShipperId;
        const apiKeyId = accountInfo?.CargofiveApiKeyId;

        if (!contactId && !addressId && !shipperId && !apiKeyId) return;

        fetchRelatedBlocks({ contactId, addressId, shipperId, apiKeyId });
    }, [
        i18n.language,
        isViewMode,
        accountInfo?.DefaultContactId,
        accountInfo?.DefaultAddressId,
        accountInfo?.DefaultShipperId,
        accountInfo?.CargofiveApiKeyId,
        fetchRelatedBlocks,
    ]);

    /**
     * Loads account integrations for the current persisted entity in Edit and View modes.
     * A guard avoids redundant requests for the same account id and language.
     */
    useEffect(() => {
        if (!isReadOrUpdateMode) return;

        /**
         * AccountId is preferred over Account name because it is stable, numeric,
         * unique, and safe to use in the backend filter.
         */
        const currentAccountId =
            accountInfo?.Id ??
            accountId ??
            sessionLine?.Id ??
            null;

        if (!currentAccountId) return;

        fetchAccountIntegrations(currentAccountId);
    }, [
        isReadOrUpdateMode,
        accountInfo?.Id,
        accountId,
        sessionLine?.Id,
        fetchAccountIntegrations,
    ]);

    // #endregion

    // #region Session refresh helpers

    /**
     * Refreshes localStorage values derived from the selected default account.
     * This ensures application-level context stays consistent after updating the active account.
     *
     * @param {number} accId - Account id used to refresh local context.
     * @returns {Promise<void>}
     */
    const refreshAccountContextInLocalStorage = useCallback(
        async (accId) => {
            try {
                const acc = await svcAccounts.getId(accId);
                if (isFailurePayload(acc)) return;

                localStorage.setItem("defaultAccountName", acc?.Name || "No Account");

                let shipperName =
                    acc?.DefaultShipper?.Name || acc?.DefaultShipperName || "";

                if (!shipperName && acc?.DefaultShipperId) {
                    const det = await svcShippers.getId(acc.DefaultShipperId);
                    if (!isFailurePayload(det)) shipperName = det?.Name || shipperName;
                }

                if (acc?.DefaultShipperId != null) {
                    localStorage.setItem("defaultShipperId", String(acc.DefaultShipperId));
                } else {
                    localStorage.removeItem("defaultShipperId");
                }

                if (shipperName) {
                    localStorage.setItem("defaultShipperName", shipperName);
                } else {
                    localStorage.removeItem("defaultShipperName");
                }

                if (acc?.CargofiveApiKeyId != null) {
                    localStorage.setItem("cargofiveApiKeyId", String(acc.CargofiveApiKeyId));
                } else {
                    localStorage.removeItem("cargofiveApiKeyId");
                }
            } catch {
                // Intentionally ignored.
            }
        },
        [svcAccounts, svcShippers]
    );

    /**
     * Refreshes menus in localStorage.
     * Both realm-scoped and legacy keys are written to keep compatibility with older consumers.
     *
     * @returns {Promise<void>}
     */
    const refreshMenusInLocalStorage = useCallback(async () => {
        try {
            const res = await svcMenus.get();
            if (res?.name !== "AxiosError" && !isFailurePayload(res)) {
                const menusJson = JSON.stringify(res || []);
                localStorage.setItem(STORAGE_USER.menus, menusJson);
                localStorage.setItem("Menus", menusJson);
            }
        } catch {
            // Intentionally ignored.
        }
    }, [svcMenus]);

    /**
     * Refreshes permissions in localStorage.
     * Both realm-scoped and legacy keys are written to keep compatibility with older consumers.
     *
     * @returns {Promise<void>}
     */
    const refreshPermissionsInLocalStorage = useCallback(async () => {
        try {
            const res = await svcPermissions.get();
            if (res?.name !== "AxiosError" && !isFailurePayload(res)) {
                const permsJson = JSON.stringify(res || []);
                localStorage.setItem(STORAGE_USER.permissions, permsJson);
                localStorage.setItem("permissions", permsJson);
            }
        } catch {
            // Intentionally ignored.
        }
    }, [svcPermissions]);

    /**
     * Clears user-realm shell state before a default-account rebootstrap.
     *
     * This reset removes dynamic tabs, selected tab data, per-tab snapshots, and transient
     * CRUD seed values so the application cannot restore account-scoped UI state after the
     * active default account changes.
     */
    const clearUserShellStateForRebootstrap = useCallback(() => {
        suppressSnapshotPersistenceRef.current = true;

        if (saveSnapshotTimer.current) {
            clearTimeout(saveSnapshotTimer.current);
            saveSnapshotTimer.current = null;
        }

        dispatch(M_StateActions.DeleteAll_M_State());

        try {
            localStorage.removeItem(USER_M_STATE_PERSIST_KEY);
        } catch {
            // Intentionally ignored.
        }
    }, [dispatch]);

    /**
     * Performs a soft session refresh after updating the currently active default account.
     *
     * The user-realm tab state is cleared before navigation because existing tabs may contain
     * account-scoped snapshots from the previous default account context.
     *
     * @param {number} accId - Account id used to refresh local session context.
     * @returns {Promise<void>}
     */
    const runSelfRebootstrap = useCallback(
        async (accId) => {
            clearUserShellStateForRebootstrap();

            await refreshAccountContextInLocalStorage(accId);
            await refreshMenusInLocalStorage();
            await refreshPermissionsInLocalStorage();

            setTimeout(() => {
                window.location.replace("/home");
            }, 0);
        },
        [
            clearUserShellStateForRebootstrap,
            refreshAccountContextInLocalStorage,
            refreshMenusInLocalStorage,
            refreshPermissionsInLocalStorage,
        ]
    );

    // #endregion

    // #region Submit and payload building

    /**
     * Builds the API payload from DynamicForm values based on the schema options.
     * Foreign keys are normalized to numeric ids or null, and boolean fields are coerced.
     *
     * In Edit mode, the inactive flag is enforced to ensure it always reaches the API payload.
     *
     * @param {object} values - Values emitted by DynamicForm.
     * @returns {object} Payload object to send to the API.
     */
    const buildPayloadFromValues = useCallback(
        (values) => {
            const options =
                Array.isArray(allOptions) && allOptions.length ? allOptions : [];

            const entries = [];

            for (const opt of options) {
                if (!opt) continue;

                const keyInValues = opt.IsForeignKey ? getFkFormKey(opt) : opt.Name;
                const payloadKey = toCamel(
                    opt.IsForeignKey ? getFkFormKey(opt) : opt.Header || opt.Name
                );

                let v;

                if (opt.IsForeignKey) {
                    const rawId = values?.[keyInValues];
                    v =
                        rawId == null || rawId === "" || Number(rawId) === 0
                            ? null
                            : Number(rawId);
                } else if (opt.Type === "boolean" || opt.Type === "bool") {
                    v = !!values?.[keyInValues];
                } else {
                    v = values?.[keyInValues];
                }

                if (v !== undefined) entries.push([payloadKey, v]);
            }

            if (
                operationRef.current === "Edit" &&
                !entries.some(([k]) => k === "inactive")
            ) {
                entries.push(["inactive", !!(values?.Inactive ?? accountInfo?.Inactive)]);
            }

            return Object.fromEntries(entries);
        },
        [allOptions, accountInfo?.Inactive]
    );

    /**
     * Handles DynamicForm submission for Create and Edit operations.
     * When the affected account matches the account currently stored in the JWT token,
     * a soft session refresh is triggered to apply the changes.
     *
     * @param {object} values - Values submitted by DynamicForm.
     * @returns {Promise<void>}
     */
    const onSubmitDynamic = useCallback(
        async (values) => {
            ctx.setIsLoad(true);

            try {
                const payload = buildPayloadFromValues(values);
                const op = (operationRef.current || "").toLowerCase();

                const currentAccId =
                    accountIdRef.current ??
                    accountInfo?.Id ??
                    sessionLineRef.current?.Id ??
                    null;

                const res =
                    op === "create"
                        ? await svcCreate.post(payload)
                        : await svcUpdate.update(currentAccId, payload);

                if (isFailurePayload(res)) {
                    toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
                    return;
                }

                toastMessages(
                    "success",
                    "",
                    op === "create" ? t("AccountCreated") : t("AccountUpdated")
                );

                const createdId = res?.Id ?? res?.id ?? res?.data?.Id ?? res?.data?.id ?? null;
                const affectedId = op === "create" ? createdId : currentAccId;

                const tokenAccId = getCurrentDefaultAccountIdFromToken();

                if (
                    affectedId != null &&
                    tokenAccId != null &&
                    Number(tokenAccId) === Number(affectedId)
                ) {
                    ctx.toastMessages(
                        "success",
                        "",
                        t("RefreshingSessionToApplyChanges") ||
                        "Refreshing your session to apply these changes..."
                    );

                    await runSelfRebootstrap(affectedId);
                    return;
                }

                closeThisTab();
            } catch (err) {
                toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
            } finally {
                ctx.setIsLoad(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            buildPayloadFromValues,
            svcCreate,
            svcUpdate,
            accountInfo?.Id,
            runSelfRebootstrap,
            closeThisTab,
        ]
    );

    /**
     * Triggers DynamicForm submission programmatically.
     * This is used by the Save button located in the header.
     */
    const triggerFormSubmit = useCallback(() => {
        formRef.current?.submit?.();
    }, []);

    /**
     * Refreshes schema and entity data for the current tab.
     * In View mode, related blocks are refreshed after the main entity reload.
     * Account integrations are refreshed in both Edit and View modes.
     *
     * @returns {Promise<void>}
     */
    const onRefresh = useCallback(async () => {
        const op = operationRef.current;
        const line = sessionLineRef.current;

        await fetchOptions(op, line);

        if (op !== "Create" && line?.Id) {
            const entity = await fetchDetails(line.Id);

            if (op === "View" && entity) {
                await fetchRelatedBlocks({
                    contactId: entity?.DefaultContactId,
                    addressId: entity?.DefaultAddressId,
                    shipperId: entity?.DefaultShipperId,
                    apiKeyId: entity?.CargofiveApiKeyId,
                });
            }

            /**
             * Account integrations are intentionally loaded by AccountId.
             * Do not use entity.Name here because names are not stable filter keys.
             */
            const integrationAccountId = entity?.Id ?? line?.Id;

            if ((op === "View" || op === "Edit") && integrationAccountId) {
                await fetchAccountIntegrations(integrationAccountId, { force: true });
            }
        }
    }, [
        fetchOptions,
        fetchDetails,
        fetchRelatedBlocks,
        fetchAccountIntegrations,
    ]);

    // #endregion

    // #region DynamicForm change persistence

    /**
     * Receives incremental value updates from DynamicForm and merges them into the per-tab snapshot.
     * Updates are ignored in View mode to reduce unnecessary Redux churn.
     *
     * FK query metadata is persisted separately in dfFkQueries to keep dropdown filter terms stable.
     *
     * @param {object} vals - Latest form values.
     * @param {object} meta - Optional metadata describing why the update was emitted.
     */
    const handleFormValuesChange = useCallback(
        (vals, meta) => {
            if (suppressSnapshotPersistenceRef.current) return;
            if ((operationRef.current || "").toLowerCase() === "view") return;
            if (!idRef.current) return;

            const isMetaOnly =
                meta?.cause === "fk-query" || meta?.cause === "fk-options";

            if (!isMetaOnly && (!vals || Object.keys(vals).length === 0)) return;

            const prevTabState =
                M_State.find((o) => o.key === idRef.current)?.value || [];

            const pick = (k) => prevTabState.find((x) => x.key === k)?.value;

            const mergedAccountInfo = {
                ...(pick("accountInfo") || accountInfo || {}),
                ...(vals || {}),
            };

            /**
             * Keep local page state aligned with form state so the latest draft is always
             * available for snapshot restore and non-form consumers.
             */
            setAccountInfo((prev) => ({ ...prev, ...(vals || {}) }));

            const updatedTabData = [
                ...prevTabState.filter(
                    (x) => x.key !== "accountInfo" && x.key !== "dfFkQueries"
                ),
                { key: "accountInfo", value: mergedAccountInfo },
                ...(meta?.cause === "fk-query"
                    ? [
                        {
                            key: "dfFkQueries",
                            value: {
                                ...(pick("dfFkQueries") || {}),
                                [meta.name]: meta.query || "",
                            },
                        },
                    ]
                    : pick("dfFkQueries") != null
                        ? [{ key: "dfFkQueries", value: pick("dfFkQueries") }]
                        : []),
            ];

            dispatch(M_StateActions.addM_State({ key: idRef.current, value: updatedTabData }));
        },
        [M_State, accountInfo, dispatch]
    );

    // #endregion

    // #region View block rendering

    /**
     * Renders a read-only DynamicForm block used to display one related entity in view mode.
     * The block is only rendered when both the schema and entity details are available.
     *
     * @param {string} titleKey - Translation key for the block title.
     * @param {Array} options - Schema options for the related entity.
     * @param {object|null} details - Related entity details.
     * @returns {JSX.Element|null} The view block or null when not applicable.
     */
    const renderViewBlock = useCallback(
        (titleKey, options, details) => {
            if (!details || !Array.isArray(options) || options.length === 0) return null;

            return (
                <div
                    className={isViewMode ? "account-view-block" : undefined}
                    style={{ gridColumn: "1 / -1", marginTop: "20px" }}
                >
                    <DynamicForm
                        key={`df-view-${titleKey}-${details?.Id ?? "na"}`}
                        mode="view"
                        schema={options}
                        data={details}
                        onSubmit={() => { }}
                        title={t(titleKey)}
                        containerClassName="alignInputsUserForm"
                    />
                </div>
            );
        },
        [isViewMode, t]
    );

    /**
     * Renders all account integration blocks.
     *
     * Each integration is rendered using the shared schema returned by the options endpoint.
     * Rendering is memoized so unchanged blocks are not rebuilt on unrelated re-renders.
     */
    const accountIntegrationBlocks = useMemo(() => {
        if (
            !Array.isArray(accountIntegrationOptions) ||
            accountIntegrationOptions.length === 0 ||
            !Array.isArray(accountIntegrations) ||
            accountIntegrations.length === 0
        ) {
            return null;
        }

        return accountIntegrations.map((integration, index) => (
            <div
                key={`account-integration-${integration?.Id ?? integration?.id ?? index}`}
                className="account-view-block"
                style={{ gridColumn: "1 / -1", marginTop: "20px" }}
            >
                <DynamicForm
                    key={`df-account-integration-${integration?.Id ?? integration?.id ?? index}`}
                    mode="view"
                    schema={accountIntegrationOptions}
                    data={integration || {}}
                    onSubmit={() => { }}
                    title={`${t("Account Integration")} ${index + 1}`}
                    containerClassName="alignInputsUserForm"
                />
            </div>
        ));
    }, [accountIntegrationOptions, accountIntegrations, t]);

    // #endregion

    // #region Render

    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t("RefreshTable")}
                            icon={<i className="pi pi-refresh iconTable"></i>}
                            onClick={onRefresh}
                            label={t("RefreshTable")}
                        />
                        {!isViewMode && (
                            <MButton
                                tooltip={t("Save")}
                                icon={<i className="pi pi-save iconTable"></i>}
                                onClick={triggerFormSubmit}
                                label={t("Save")}
                            />
                        )}
                    </>
                }
            />

            <div style={{ padding: "20px" }}>
                <div className="firstDivContainer">
                    <div className={isReadOrUpdateMode ? "account-view-block" : undefined}>
                        <DynamicForm
                            key={`df-main-${id}-${String(operation || "").toLowerCase()}-${formRestoreVersion}`}
                            formRef={formRef}
                            mode={(operation || "View").toLowerCase()}
                            schema={allOptions}
                            data={accountInfo}
                            onSubmit={onSubmitDynamic}
                            includeInCreate={includeInCreate_Account}
                            title={t("Account Information")}
                            containerClassName="alignInputsUserForm"
                            onValuesChange={!isViewMode ? handleFormValuesChange : null}
                            fkInitialQueries={fkInitialQueries}
                        />
                    </div>

                    {isViewMode && (
                        <>
                            {accountInfo?.DefaultAddressId &&
                                renderViewBlock(
                                    "Default Address",
                                    addressOptions,
                                    addressDetail
                                )}

                            {accountInfo?.DefaultShipperId &&
                                renderViewBlock(
                                    "Default Shipper",
                                    shipperOptions,
                                    shipperDetail
                                )}

                            {accountInfo?.DefaultContactId &&
                                renderViewBlock(
                                    "Default Contact",
                                    contactOptions,
                                    contactDetail
                                )}
                        </>
                    )}

                    {isReadOrUpdateMode && accountIntegrationBlocks}
                </div>
            </div>
        </>
    );

    // #endregion
}

AccountConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func,
};

// #endregion